/** Lowercase metric_exclusions keys for every category (e.g. compliancePercentage → compliancepercentage). */
export function normalizeMetricExclusions(metricExclusions: any) {
  if (!metricExclusions || typeof metricExclusions !== "object") {
    return metricExclusions ?? null;
  }
  return Object.fromEntries(
    Object.entries(metricExclusions).map(([category, categoryExclusions]) => {
      if (!categoryExclusions || typeof categoryExclusions !== "object") {
        return [category, categoryExclusions];
      }
      return [
        category,
        Object.fromEntries(
          Object.entries(categoryExclusions as Record<string, unknown>).map(
            ([metricKey, config]) => [metricKey.toLowerCase(), config]
          )
        ),
      ];
    })
  );
}

/** Lowercase object keys (e.g. brandStrategy → brandstrategy). */
export function lowercaseKeys(obj: any): any {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key.toLowerCase(), value])
  );
}

/** Lowercase `key` on dashboard_insights / insights metric items. */
export function lowercaseMetricItemKeys<T extends { key?: string }>(
  items: T[] | null | undefined
): T[] | null | undefined {
  if (!Array.isArray(items)) return items;
  return items.map((item) =>
    item?.key != null ? { ...item, key: String(item.key).toLowerCase() } : item
  );
}

function normalizeReportMetrics(report: any): any {
  if (!report || typeof report !== "object") return report;
  const next = { ...report };
  if (next.constant_metrics) next.constant_metrics = lowercaseKeys(next.constant_metrics);
  if (next.variable_metrics) next.variable_metrics = lowercaseKeys(next.variable_metrics);
  if (next.report && typeof next.report === "object" && !Array.isArray(next.report)) {
    next.report = normalizeReportMetrics(next.report);
  }
  return next;
}

export function normalizeProfilePayload(data: any): any {
  if (!data?.profile) return data;
  return {
    ...data,
    profile: {
      ...data.profile,
      metric_exclusions: normalizeMetricExclusions(data.profile.metric_exclusions),
    },
  };
}

export function normalizeDashboardInsightsPayload(data: any): any {
  if (!data || typeof data !== "object") return data;
  return { ...data, metrics: lowercaseMetricItemKeys(data.metrics) };
}

export function normalizeSessionReportPayload(data: any): any {
  if (!data || typeof data !== "object") return data;
  return {
    ...data,
    report: data.report ? normalizeReportMetrics(data.report) : data.report,
  };
}

export function normalizeDetailedReportPayload(data: any): any {
  if (!data || typeof data !== "object") return data;
  return {
    ...data,
    aggregatedReportFeedback: lowercaseKeys(data.aggregatedReportFeedback),
    aggregatedReportScores: lowercaseKeys(data.aggregatedReportScores),
    report: Array.isArray(data.report)
      ? data.report.map((item: any) =>
          item?.report
            ? { ...item, report: normalizeReportMetrics(item.report) }
            : item
        )
      : data.report,
  };
}

export function normalizeInsightsPayload(data: any): any {
  if (!data || typeof data !== "object") return data;
  const next = { ...data };
  for (const key of Object.keys(next)) {
    if (/^insights/i.test(key) && Array.isArray(next[key])) {
      next[key] = lowercaseMetricItemKeys(next[key]);
    }
  }
  return next;
}

/** Look up averageBrandStrategy-style scores with a lowercase metric key. */
export function getAverageMetricScore(scores: any, metric: string): number {
  if (!scores || !metric) return 0;
  return Number(scores[`average${metric}`]) || 0;
}

/** Default metric titles when dashboard_insights is missing or empty. */
export const DEFAULT_REPORT_INSIGHTS: Record<string, string> = {
  compliancepercentage: "Compliance",
  brandstrategy: "Brand Strategy Alignment",
  productknowledge: "Product Knowledge",
  questiontechniques: "Questioning Techniques",
  languageclarity: "Grammar",
  tone: "Tone",
  vocabulary: "Vocabulary and Terminology",
  speaklistenratio: "Active Listening Skills",
  sentiment: "Sentiment",
  filler: "Filler",
};

export const getReportInsights = (
  dashboardMetrics: Array<{ key: string; title: string }> | null | undefined
): Record<string, string> => {
  if (!dashboardMetrics?.length) return { ...DEFAULT_REPORT_INSIGHTS };
  return Object.fromEntries(
    dashboardMetrics.map((m) => [m.key.toLowerCase(), m.title])
  );
};
